import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BoldHeader from './BoldHeader';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setErrors({
        submit: 'Please fill in all fields'
      });
      return;
    }

    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        submit: error.message || 'Login failed. Please check your credentials.'
      });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-jet-black">
      <BoldHeader />
      
      <div className="flex items-center justify-center min-h-screen pt-20">
        <div className="max-w-md w-full mx-6">
          <div className="text-center mb-12">
            <h1 className="display-hero mb-6">
              READY TO <span className="text-neon-green">TRANSFORM</span>?
            </h1>
            <p className="text-energy text-xl mb-8">
              Join thousands who've unlocked their potential with AI coaching. 
              Your transformation starts NOW.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 rounded-3xl p-8 neon-border backdrop-blur-sm">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-power">
                <span className="text-white font-black text-2xl">AI</span>
              </div>
              <h2 className="heading-bold text-white mb-4">
                UNLEASH YOUR FULL POTENTIAL
              </h2>
              <p className="text-gray-300 text-sm mb-4">
                Sign in to access your personal AI coach and continue your transformation
              </p>
            </div>

            {errors.submit && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-2xl">
                <p className="text-red-200 text-sm text-center">{errors.submit}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 border-gray-600 focus:border-electric-blue/50 transition-all"
                />
              </div>

              <div>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 border-gray-600 focus:border-electric-blue/50 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-power py-4 text-lg mb-6 hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {loading ? '🚀 SIGNING YOU IN...' : '🚀 START MY TRANSFORMATION'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
            </form>

            <div className="grid grid-cols-2 gap-4 text-center text-sm mb-6">
              <div className="text-gray-400">
                <div className="text-neon-green font-bold text-lg">24/7</div>
                <div>AI Support</div>
              </div>
              <div className="text-gray-400">
                <div className="text-electric-blue font-bold text-lg">100%</div>
                <div>Personalized</div>
              </div>
            </div>

            <div className="text-center mt-8 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm mb-4">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/register')}
                  className="text-neon-green font-bold hover:text-electric-blue transition-colors"
                >
                  Join the Elite
                </button>
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                <span>🔒 Bank-Level Security</span>
                <span>•</span>
                <span>⚡ Instant Access</span>
                <span>•</span>
                <span>✨ Free Forever</span>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;